import {forwardRef, useCallback, useMemo} from 'react';
import {createMask, MaskedDynamic, type InputMask, type InputMaskElement} from 'imask';
import {IMask, IMaskInput, IMaskInputProps, type ExtractMaskOpts} from 'react-imask';
import {twMerge} from 'tailwind-merge';
import {useFormControlContext} from '@/components/form/form-control/form-control';
import {useTextInputClassName} from '@/components/form/hooks/use-text-input-classname';
import environment from '@/environment';
import {ENGLISH_LOCALE, FRENCH_LOCALE, SupportedLocale} from '@/initializers/i18next';
import {FIELD_TYPES} from '@/model/types';

const DEFAULT_REPLACEMENTS = {'&': /[0-9A-Z]/, '_': /./, '#': /\d/, '!': /[A-Z]/, '@': /[a-zA-Z]/} as const;
const PLACEHOLDER_CHARACTER_PER_LOCALE = {
  YEAR: {
    [ENGLISH_LOCALE]: 'Y',
    [FRENCH_LOCALE]: 'A',
  },
  MONTH: {
    [ENGLISH_LOCALE]: 'M',
    [FRENCH_LOCALE]: 'M',
  },
  DAY: {
    [ENGLISH_LOCALE]: 'D',
    [FRENCH_LOCALE]: 'J',
  },
} as const;

type InputProps = IMaskInputProps<HTMLInputElement> & {
  labelWithinInput?: string;
  locale?: SupportedLocale;
  isUppercasedMask?: boolean;
};

const formatDate = (date: Date) => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  const paddedDay = day < 10 ? '0' + day : String(day);
  const paddedMonth = month < 10 ? '0' + month : String(month);

  return [year, paddedMonth, paddedDay].join('-');
};

const parseDate = (dateString: string) => {
  const [year, month, day] = dateString.split('-');
  return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
};

export default forwardRef<HTMLInputElement, InputProps>(function Input(
  {type = 'text', value, mask, readOnly, labelWithinInput, locale = ENGLISH_LOCALE, isUppercasedMask, ...props},
  ref,
) {
  const {isDisabled} = useFormControlContext();

  const isFieldTypeDate = type === FIELD_TYPES.DATE;
  const isFieldTypeTel = type === FIELD_TYPES.TEL;
  const isMaskVisible = isFieldTypeDate || isFieldTypeTel ? true : false;

  const blocks = {
    D: {
      mask: IMask.MaskedRange,
      from: 1,
      to: 31,
      maxLength: 2,
      placeholderChar: PLACEHOLDER_CHARACTER_PER_LOCALE.DAY[locale],
    },
    M: {
      mask: IMask.MaskedRange,
      from: 1,
      to: 12,
      maxLength: 2,
      placeholderChar: PLACEHOLDER_CHARACTER_PER_LOCALE.MONTH[locale],
    },
    Y: {
      mask: IMask.MaskedRange,
      from: 1900,
      to: 9999,
      placeholderChar: PLACEHOLDER_CHARACTER_PER_LOCALE.YEAR[locale],
    },
  };

  const uuid = crypto.randomUUID();

  const inputMergedClasses = useTextInputClassName({readOnly, isLabelWithingInput: !!labelWithinInput});

  const prepareCharacters = useCallback(
    (value: string) => {
      if (!value || !isUppercasedMask) return value;

      return value.toUpperCase();
    },
    [isUppercasedMask],
  );

  const dataValue = useMemo(() => {
    if (!environment.DEBUG) return;

    return value?.toString() ?? '';
  }, [value]);

  const {onAccept} = props;
  const acceptDateInput = useCallback(
    (
      value: string,
      maskRef: InputMask<ExtractMaskOpts<InputMaskElement, Record<string, unknown>>>,
      event: InputEvent | undefined,
    ) => {
      // Prevent initial mask from being applied to the input
      const acceptedValue = !maskRef._unmaskedValue ? '' : value;
      onAccept?.(acceptedValue, maskRef, event);
    },
    [onAccept],
  );

  // If the phone number allow an optional country code (like fax number), we rewrite the mask to support dispatch event to
  // select the correct mask when the user types.
  const [masks, dispatch] = useMemo(() => {
    if (type === 'tel' && mask === '[1]-(###) ###-####') {
      const masks = [
        createMask({mask: '0-(000)-000-0000', placeholderChar: '#', lazy: false, startsWith: '1'}),
        createMask({mask: '(000)-000-0000', placeholderChar: '#', lazy: false, startsWith: ''}),
      ];

      const dispatch = (appended: string, dynamicMasked: MaskedDynamic<string>) => {
        const number = (dynamicMasked.value + appended).replace(/\D/g, '');
        const masks = dynamicMasked.compiledMasks;

        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        const mask = masks.find((mask: any) => number.indexOf(mask.startsWith) === 0) || masks[0];

        return mask;
      };

      return [masks, dispatch];
    } else {
      const dispatch = (_char: string, dynamicMasked: MaskedDynamic<string>) => dynamicMasked.compiledMasks[0];
      return [mask, dispatch];
    }
  }, [mask, type]);

  return (
    <div className="flex w-full flex-col">
      {isFieldTypeDate ? (
        // @ts-expect-error - Typecheck complains about `mask` not being the
        // correct type but everything works as expected and is exactly like the
        // documentation…
        <IMaskInput
          {...props}
          type="text"
          inputRef={ref}
          // Override `onChange` since the types don't match with the HTMLElement
          // types and we don't need it anyway
          onChange={() => {}}
          id={uuid}
          autoComplete="off"
          data-1p-ignore
          disabled={isDisabled}
          readOnly={readOnly}
          className={twMerge(inputMergedClasses)}
          value={value?.toString() ?? ''}
          data-value={dataValue}
          onAccept={acceptDateInput}
          mask={Date}
          pattern="Y-`M-`D"
          // Override `min` since the types don't match with the HTMLElement
          // types and we don't need it anyway
          min={undefined}
          max={new Date(3000, 12, 31)}
          lazy={!isMaskVisible}
          blocks={blocks}
          format={formatDate}
          parse={parseDate}
        />
      ) : (
        // @ts-expect-error - Typecheck complains about `mask` not being the
        // correct type but everything works as expected and is exactly like the
        // documentation…
        <IMaskInput
          {...props}
          type={type ?? 'text'}
          inputRef={ref}
          // Override `onChange` since the types don't match with the HTMLElement
          // types and we don't need it anyway
          onChange={() => {}}
          id={uuid}
          unmask={Boolean(isFieldTypeTel)}
          autoComplete="off"
          data-1p-ignore
          disabled={isDisabled}
          readOnly={readOnly}
          className={twMerge(inputMergedClasses)}
          value={value?.toString() ?? ''}
          data-value={dataValue}
          onAccept={onAccept}
          mask={masks}
          dispatch={dispatch}
          prepareChar={prepareCharacters}
          definitions={DEFAULT_REPLACEMENTS}
          lazy={!isMaskVisible}
        />
      )}

      {labelWithinInput && (
        <span className="pointer-events-none absolute inset-0 left-4 inline-flex items-center text-sm font-bold leading-none text-charcoal peer-hover:text-violet peer-focus:text-violet">
          {labelWithinInput}
        </span>
      )}
    </div>
  );
});
